// Activity Layer client API — read-only audit history + audit-event writes.
//
// This is the LIVE surface today:
//   * listActivityEvents → list_activity_events(entity_type, entity_id,
//     limit). Audit reads for a single entity; rows carry
//     mentioned_profile_ids[] + mentioned_profile_names[]. Used by
//     RecordActivityLog (the read-only Activity log on record pages).
//   * recordActivityEvent / recordFieldChange / recordStatusChange →
//     record_activity_event(...). Audit writes (field.updated,
//     status.changed, record.created/deleted/restored). Not transactional with
//     the caller's mutation; migration 226 makes a lost-response retry safe.
//   * renderMentionSegments → pure renderer that chips "@Name" spans by
//     matching mentioned_profile_names[]; reused by the Comments layer.
//
// The legacy global Activity composer (ActivityModal/ActivityPanel) was
// retired. Its client helpers — countActivityForEntity, postActivityComment,
// editActivityEvent, deleteActivityEvent — were removed. User discussion now
// lives in CommentsSection (the comments_foundation layer, mig 071); Activity
// is read-only audit/system history. The underlying post/edit/delete/count
// SECDEF RPCs still exist in PROD (historical migrations 058/060) but have no
// client caller.
//
// The platform contract: clients NEVER hit `.from('activity_events')` or
// `.from('activity_mentions')` directly. RLS lockdown on the tables blocks it
// anyway (REVOKE ALL from authenticated), but the static lock also rejects any
// such reference in src/. The RPC layer is the only path; the SECDEF resolver
// re-checks the source entity's read gate.
//
// Mention rendering (mig 060 contract): the visible body is freeform plain
// text — users only ever see "@DisplayName", never uuids. renderMentionSegments
// chips "@Name" spans by matching mentioned_profile_names[] returned from
// list_activity_events. Best-effort: ambiguous names (two teammates with the
// same display name) all render as chips with the first matching profile id.

import {newClientSubmissionId} from './clientSubmissionId.js';
import type {RpcEnvelope, SupabaseClient} from './supabaseTypes.js';

export const ACTIVITY_CHANGE_EVENT = 'wcf-activity-change';

export function fireActivityChangeEvent(entityType: string, entityId: string): void {
  if (typeof window === 'undefined' || !window.dispatchEvent) return;
  try {
    window.dispatchEvent(new CustomEvent(ACTIVITY_CHANGE_EVENT, {detail: {entityType, entityId}}));
  } catch (_e) {
    /* CustomEvent not supported in some test envs */
  }
}

/**
 * List activity events for one entity (newest first). Each row carries
 * both `mentioned_profile_ids` and `mentioned_profile_names` arrays
 * (same length, same order) for chip rendering. Soft-deleted rows are
 * INCLUDED so the panel can render "(comment deleted)" placeholders.
 */
export async function listActivityEvents(
  sb: SupabaseClient | null | undefined,
  entityType: string | null | undefined,
  entityId: string | null | undefined,
  {limit = 50, signal}: {limit?: number; signal?: AbortSignal} = {},
): Promise<unknown[]> {
  if (!sb) return [];
  if (!entityType || !entityId) return [];
  const request = sb.rpc('list_activity_events', {
    p_entity_type: entityType,
    p_entity_id: entityId,
    p_limit: limit,
  });
  // Query-owned mount reads can pass their AbortSignal through this common
  // transport; existing action/event callers retain the same no-signal path.
  const response = signal && typeof request.abortSignal === 'function' ? request.abortSignal(signal) : request;
  const {data, error} = await response;
  if (error) throw new Error(`listActivityEvents: ${error.message || String(error)}`);
  return data || [];
}

// The entity an activity write is about. Optional + nullable because
// recordActivityEvent VALIDATES them at runtime and throws its own message —
// making them required here would move that check to compile time for the
// converted callers only, and silently leave the JavaScript ones unguarded.
export type ActivityEntityRef = {
  entityType?: string | null;
  entityId?: string | null;
  entityLabel?: string | null;
};

// `payload` crosses the boundary as jsonb the RPC validates; its keys are the
// migration's contract, not this client's.
export type ActivityEventInput = ActivityEntityRef & {
  eventType?: string | null;
  body?: string | null;
  payload?: Record<string, unknown> | null;
};

// ── Activity Layer: general event recording (mig 066) ───────────────────
//
// Platform write path for the WCF Activity Layer. Every user-initiated
// mutation should flow through recordActivityEvent or one of the
// convenience wrappers below. Do NOT use for autosave ticks or
// intermediate state — only for intentional saved changes.
//
// Allowed event types (server-enforced):
//   field.updated, status.changed, record.created,
//   record.deleted, record.restored

// Ship this client only after migration 226. The request identity belongs to
// one intended event, and stays the same when the transport loses its answer.
// A caller retrying a whole operation can persist payload.request_id itself.
const ACTIVITY_TRANSPORT_RETRIES = 2;

export async function recordActivityEvent(
  sb: SupabaseClient | null | undefined,
  {entityType, entityId, eventType, entityLabel, body, payload}: ActivityEventInput,
): Promise<RpcEnvelope> {
  if (!sb) throw new Error('recordActivityEvent: sb required');
  if (!entityType || !entityId) throw new Error('recordActivityEvent: entityType + entityId required');
  if (!eventType) throw new Error('recordActivityEvent: eventType required');
  // Snapshot nested values too: a caller changing its draft while a request is
  // in flight must not change the content associated with this retry identity.
  const eventPayload: Record<string, unknown> = JSON.parse(JSON.stringify(payload || {}));
  const suppliedId = eventPayload.request_id;
  if (suppliedId != null && typeof suppliedId !== 'string') {
    throw new Error('recordActivityEvent: request_id must be text');
  }
  eventPayload.request_id = (typeof suppliedId === 'string' && suppliedId.trim()) || newClientSubmissionId();
  const request = {
    p_entity_type: entityType,
    p_entity_id: entityId,
    p_event_type: eventType,
    p_entity_label: entityLabel || null,
    p_body: body || null,
    p_payload: eventPayload,
  };
  for (let attempt = 0; attempt <= ACTIVITY_TRANSPORT_RETRIES; attempt++) {
    let response;
    try {
      response = await sb.rpc('record_activity_event', request);
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') throw error;
      if (attempt < ACTIVITY_TRANSPORT_RETRIES) continue;
      throw new Error(`recordActivityEvent: ${error instanceof Error ? error.message : String(error)}`);
    }
    const {data, error} = response;
    if (error) {
      // A PostgreSQL/PostgREST code is an actual refusal, not a lost answer.
      // Retry only a transport failure or a gateway with no database result.
      const noAnswer = !error.code && [0, 502, 503, 504].includes(response.status);
      if (noAnswer && attempt < ACTIVITY_TRANSPORT_RETRIES) continue;
      throw new Error(`recordActivityEvent: ${error.message || String(error)}`);
    }
    if (!data || data.ok !== true || typeof data.event_id !== 'string' || !data.event_id) {
      throw new Error('recordActivityEvent: audit write was not confirmed');
    }
    fireActivityChangeEvent(entityType, entityId);
    return data;
  }
  throw new Error('recordActivityEvent: audit write was not confirmed');
}

// `changes` is `unknown` because the Array.isArray guard below is real: several
// callers hand over whatever a diff helper produced, and a non-array there is a
// documented no-op rather than a crash. `from`/`to` stay `unknown` — they are
// arbitrary column values from any record page, coerced by the string
// concatenation exactly as before.
export type FieldChange = {
  field?: string;
  label?: string;
  from?: unknown;
  to?: unknown;
  old_present?: boolean;
  new_present?: boolean;
};

export function buildFieldChangeSummary(changes: unknown): {body: string; changes: FieldChange[]} {
  if (!Array.isArray(changes) || changes.length === 0) return {body: '', changes: []};
  const parts = changes.map((c: FieldChange) => {
    const label = c.label || c.field;
    if (!c.new_present && c.old_present) return 'Cleared ' + String(label);
    if (c.new_present && !c.old_present) return 'Set ' + String(label);
    const excerpt = typeof c.to === 'string' && c.to.length > 80 ? c.to.slice(0, 80) + '…' : c.to;
    return 'Updated ' + String(label) + (excerpt != null ? ': ' + String(excerpt) : '');
  });
  return {body: parts.join('; '), changes};
}

export async function recordFieldChange(
  sb: SupabaseClient | null | undefined,
  {entityType, entityId, entityLabel, changes}: ActivityEntityRef & {changes?: FieldChange[] | null},
): Promise<RpcEnvelope> {
  const summary = buildFieldChangeSummary(changes);
  return recordActivityEvent(sb, {
    entityType,
    entityId,
    eventType: 'field.updated',
    entityLabel,
    body: summary.body,
    payload: {changes: summary.changes},
  });
}

export async function recordStatusChange(
  sb: SupabaseClient | null | undefined,
  {entityType, entityId, entityLabel, from, to}: ActivityEntityRef & {from?: string | null; to?: string | null},
): Promise<RpcEnvelope> {
  const body = 'Status changed from ' + (from || '(none)') + ' to ' + (to || '(none)');
  return recordActivityEvent(sb, {
    entityType,
    entityId,
    eventType: 'status.changed',
    entityLabel,
    body,
    payload: {changes: [{field: 'status', label: 'Status', from, to, old_present: !!from, new_present: !!to}]},
  });
}

// ── Mention rendering helper (pure, exported for reuse + testing) ──────

// One rendered piece of a comment body. `profileId` includes `undefined`
// because it comes from `Map.get`, whose miss is undefined; the key always
// exists here (it came from the same map's keys), so the type records what the
// Map API can return rather than asserting the invariant away.
export type MentionSegment =
  | {type: 'text'; text: string}
  | {type: 'mention'; display: string; profileId: string | null | undefined};

/**
 * Split a comment body into renderable segments. Each "@Name" span whose
 * Name matches an entry in mentionedProfileNames becomes a chip; the
 * rest is plain text.
 *
 * Output is an array of:
 *   {type: 'text', text: '...'}
 *   {type: 'mention', display: 'Mak', profileId: 'uuid-or-null'}
 *
 * Multi-word names work (the picker inserts the full literal string).
 * Longest-match-wins so "@Test Admin" doesn't get partially eaten by
 * "@Test" if both names exist.
 *
 * Ambiguous case: if two mentioned profiles share the same display
 * name, all "@Name" spans chip with the FIRST profileId in the array.
 * The notification fan-out (server-side) is unaffected — every uuid
 * the user picked is notified separately. profileId may be null if
 * the renderer is called without the ids array (text-only chipping).
 */
export function renderMentionSegments(
  body: unknown,
  mentionedProfileNames: readonly (string | null | undefined)[] = [],
  mentionedProfileIds: readonly (string | null | undefined)[] = [],
): MentionSegment[] {
  if (!body || typeof body !== 'string') return [];
  if (!Array.isArray(mentionedProfileNames) || mentionedProfileNames.length === 0) {
    return [{type: 'text', text: body}];
  }
  // Pair name → first profile id. Names array can contain duplicates;
  // keep the first occurrence so the chip carries a stable id.
  const nameToId = new Map<string, string | null>();
  for (let i = 0; i < mentionedProfileNames.length; i++) {
    const n = mentionedProfileNames[i];
    if (!n || nameToId.has(n)) continue;
    nameToId.set(n, mentionedProfileIds[i] || null);
  }
  // Longest-match-wins so multi-word names beat shorter substrings.
  const sortedNames = [...nameToId.keys()].filter((n) => n.length > 0).sort((a, b) => b.length - a.length);
  if (sortedNames.length === 0) return [{type: 'text', text: body}];

  const out: MentionSegment[] = [];
  let i = 0;
  while (i < body.length) {
    if (body[i] !== '@') {
      // Accumulate plain text until the next '@'.
      let j = i + 1;
      while (j < body.length && body[j] !== '@') j++;
      out.push({type: 'text', text: body.slice(i, j)});
      i = j;
      continue;
    }
    // body[i] === '@' — try to match a known mention name starting here.
    let matched: string | null = null;
    for (const name of sortedNames) {
      if (body.startsWith('@' + name, i)) {
        // Word-boundary on the right side: next char must be end-of-string
        // or non-name punctuation so "@Nick" doesn't match inside "@Nickname".
        const tailIdx = i + 1 + name.length;
        const tail = body[tailIdx];
        if (tail === undefined || /[\s.,!?;:)\]}'"-]/.test(tail)) {
          matched = name;
          break;
        }
      }
    }
    if (matched) {
      out.push({type: 'mention', display: matched, profileId: nameToId.get(matched)});
      i += 1 + matched.length;
    } else {
      // Lone @ not followed by a known mention — render as plain text.
      out.push({type: 'text', text: '@'});
      i += 1;
    }
  }
  // Collapse adjacent text segments for cleaner output.
  const collapsed: MentionSegment[] = [];
  for (const seg of out) {
    const last = collapsed[collapsed.length - 1];
    if (seg.type === 'text' && last && last.type === 'text') {
      last.text += seg.text;
    } else {
      collapsed.push(seg);
    }
  }
  return collapsed;
}
